import { useEffect, useMemo, useRef, useState } from 'react';

import { useLocation, useNavigate, useParams } from 'react-router-dom';

import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import { useCurrencies } from 'api/useCurrencies';
import useOrders from 'api/useOrders';
import usePermissions from 'api/usePermissions';
import useUsers from 'api/useUsers';
import { Project, User } from 'common/contracts';
import { EPermissionAction, EPermissionGroup } from 'common/permissions.types';
import { DATE_TIME_FORMAT } from 'constants/constants';
import {
  EButtonColor,
  EFeatureFlag,
  ELocalStorageKeys,
  EOrderStatus
} from 'constants/enums';
import dayjs from 'dayjs';
import { StatusLabel } from 'design-system/StatusLabel/StatusLabel';
import { flatten } from 'flat';
import {
  capitalizeFirstLetter,
  getFormattedNumberWithCurrency
} from 'utils/formattingUtil';
import { parseJson } from 'utils/jsonUtil';
import { localStorageUtil } from 'utils/localStorageUtil';
import { permissionsUtil } from 'utils/permissionsUtil';

import AcContentWrapper from 'components/AcContentWrapper/AcContentWrapper';
import AcSeparator from 'components/AcSeparator/AcSeparator';
import AcViewWrapper from 'components/AcViewWrapper/AcViewWrapper';
import DataTable from 'components/DataTable/DataTable';
import DialogModal from 'components/Dialog/Dialog';
import ErrorBox from 'components/ErrorBox/ErrorBox';
import {
  chargeFailedFreeOrder,
  statusDictionary
} from 'components/Orders/OrdersTable/OrdersTable.types';
import PageTopBar from 'components/Topbar/PageTopBar';

import { EToasterStatus, showToast } from '../../utils/showToast';

import DisputeEvidence from './dispute-evidence/DisputeEvidence';

import './style.scss';
const OrderDetailsCell = ({
  header,
  value,
  tooltip = ''
}: {
  header: string;
  value: string | number | React.ReactNode;
  tooltip?: string;
}) => {
  return (
    <Stack gap={0.5} mb={2}>
      <Typography
        className="cell-title"
        fontFamily="'Montserrat', sans-serif"
        fontSize={12}
        fontWeight={600}
      >
        <Tooltip arrow placement="right" title={tooltip}>
          <Box component="span">{header}</Box>
        </Tooltip>
      </Typography>
      <Typography
        className="cell-value"
        fontFamily="'Montserrat', sans-serif"
        fontSize={14}
        fontWeight={400}
      >
        {value}
      </Typography>
    </Stack>
  );
};

const OrderDetails = ({ isFree }: { isFree: boolean }) => {
  const { orderId } = useParams();
  const { search, state } = useLocation();

  const orderParams = useMemo(() => {
    if (state) return state;

    const params = new URLSearchParams(search);

    return {
      publisherId: params.get('publisherId'),
      state: params.get('state')
    };
  }, [search, state]);

  const [showRefundDialog, setShowRefundDialog] = useState<boolean>(false);

  const iframeRef = useRef<HTMLIFrameElement>(null);

  const [isDeleting, setIsDeleting] = useState(false);
  const navigate = useNavigate();
  const [orderStatus, setOrderStatus] = useState<string>(
    orderParams.state || ''
  );

  const [isOverThreshold, setIsOverThreshold] = useState<boolean>(true);
  const [isRefundHidden, setIsRefundHidden] = useState<boolean>(true);
  const [generateReceipt, setGenerateReceipt] = useState<boolean>(false);
  const [isGeneratingReceipt, setIsGeneratingReceipt] =
    useState<boolean>(false);

  useEffect(() => {
    window.addEventListener('message', (event) => {
      if (event.data === 'receipt-generated') {
        if (isGeneratingReceipt) {
          showToast({
            message:
              'Receipt generated successfully, check your downloads folder',
            status: EToasterStatus.SUCCESS
          });
        }
        setIsGeneratingReceipt(false);
        setGenerateReceipt(false);
      }
    });
  }, [isGeneratingReceipt]);

  const { getOrderDetails: order, makeRefund } = useOrders(
    orderId,
    undefined,
    undefined,
    { selectedProjects: orderParams.publisherId }
  );

  const { currencyCodeToCurrencyMap } = useCurrencies();

  const { fetchFeatureFlags } = useUsers({
    publisherId:
      !order?.isLoading && order.isSuccess
        ? order?.data?.offerSnapshot?.publisherId
        : undefined,
    enableFeatureFlags: !order?.isLoading && order.isSuccess
  });

  const multiplier = currencyCodeToCurrencyMap.get(
    order.data?.currency || ''
  )?.multiplier;
  const showDisputeEvidences =
    Boolean(order.data?.dispute) &&
    fetchFeatureFlags.data?.featureFlags?.[
      EFeatureFlag.DASHBOARD_DISPUTE_EVIDENCES
    ];

  const { getPermissions } = usePermissions();
  const permissions = getPermissions();

  const projectsDetails = localStorageUtil.getAny<User>(
    ELocalStorageKeys.USER_DETAILS
  )?.projects;
  const getCurrentProject = (publisherId: string): Project | undefined =>
    projectsDetails?.find((item) => item.publisherId === publisherId);

  useEffect(() => {
    order.refetch();
  }, []);

  useEffect(() => {
    if (order.data) {
      const today = dayjs();
      setIsOverThreshold(
        today.diff(order.data.createdAt, 'day') >
          order.data?.refundThresholdDays
      );
      setOrderStatus(order.data.status);
    }
  }, [order.data]);

  const getPaymentMethod = (): string => {
    if (order.data?.paymentMethod) {
      return order.data?.paymentMethod.secondaryMethod
        ? capitalizeFirstLetter(
            `${order.data?.paymentMethod.primaryMethod} (${order.data?.paymentMethod.secondaryMethod})`
          )
        : capitalizeFirstLetter(order.data?.paymentMethod.primaryMethod);
    }

    return '-';
  };

  const taxPercentage =
    order?.data?.taxRatePoints &&
    order?.data?.taxRatePoints.div(100).toNumber();

  const sessionMetadata = order?.data?.offerSnapshot.sessionMetadata || {};
  const parsedSessionMetadata = parseJson(sessionMetadata);
  const sessionMetadataEntries = Object.entries(
    flatten(parsedSessionMetadata) as Record<string, any>
  ).map(([key, value]) => [key, typeof value === 'object' ? '' : value]);

  const formatReceiptId = (receiptId: string): string => {
    if (!receiptId) return '-';
    return receiptId.replace(/(\d{3})(?=\d)/g, '$1-');
  };

  useEffect(() => {
    const per = !permissionsUtil.isActionEnabled(
      permissions,
      EPermissionGroup.ORDERS,
      EPermissionAction.REFUND,
      orderParams.publisherId
    );

    setIsRefundHidden(
      order.isLoading ||
        (orderStatus !== EOrderStatus.payment_succeed &&
          orderStatus !== EOrderStatus.charge_succeed &&
          orderStatus !== EOrderStatus.charge_failed &&
          orderStatus !== EOrderStatus.payment_dispute_won &&
          orderStatus !== EOrderStatus.payment_dispute_inquiry_won &&
          orderStatus !== EOrderStatus.payment_refund_cancelled) ||
        isOverThreshold ||
        !order.data?.refundThresholdDays ||
        per
    );
  }, [
    isOverThreshold,
    order.data?.refundThresholdDays,
    order.isLoading,
    orderStatus,
    permissions,
    orderParams?.publisherId
  ]);

  const handleBackBtnClick = () => {
    let navigationPath = '/orders';
    if (isFree) {
      navigationPath = navigationPath + '/free';
    }
    navigate(navigationPath);
  };

  const isSubscriptionsAvailable =
    fetchFeatureFlags.data?.featureFlags?.[
      EFeatureFlag.DASHBOARD_SUBSCRIPTIONS
    ];

  return (
    <AcViewWrapper
      header={
        <PageTopBar
          headline={`Order ID ${orderId}`}
          buttons={[
            {
              text: 'Refund',
              tooltip: isOverThreshold ? '' : "Order can't be refunded",
              action: () => setShowRefundDialog(true),
              hidden: isRefundHidden
            }
          ]}
          backFunction={handleBackBtnClick}
        />
      }
    >
      {generateReceipt && (
        <Stack
          width="600px"
          height="100vh"
          justifyContent="center"
          alignItems="center"
          position="absolute"
          top={'-200vw'}
          left={'-200vh'}
          zIndex={99999999}
        >
          <iframe
            ref={iframeRef}
            onLoad={() => {
              iframeRef.current?.contentWindow?.postMessage(
                JSON.stringify({
                  products: order?.data?.products,
                  recieptId: order?.data?.receiptId?.replace(
                    /(\d{3})(?=\d)/g,
                    '$1-'
                  ),
                  orderId: orderId,
                  orderStatus: orderStatus,
                  orderDate: dayjs(order?.data?.createdAt)
                    .utc()
                    .format(DATE_TIME_FORMAT),
                  orderAmount:
                    order?.data?.amountInDollar && multiplier
                      ? `${order.data.amountNetTotal.div(multiplier).toNumber()} ${order.data.currency}`
                      : 'N/A',
                  orderCurrency: order.data?.currency,
                  projectName: order?.data?.offerSnapshot?.publisherId
                    ? getCurrentProject(order?.data?.offerSnapshot?.publisherId)
                        ?.publisherName ?? '-'
                    : '-',
                  projectId: order?.data?.offerSnapshot?.publisherId,
                  paymentMethod: order?.data?.paymentMethod?.primaryMethod,
                  last4Digits: '1234',
                  taxRate: order?.data?.taxRatePoints.div(100).toNumber(),
                  tax:
                    order?.data?.taxTotal !== undefined && multiplier
                      ? `${order.data.taxTotal.div(multiplier).toNumber()} ${order.data.currency}`
                      : 'N/A',
                  subtotal:
                    order?.data?.amountNetTotal !== undefined && multiplier
                      ? order?.data?.amountNetTotal.div(multiplier).toNumber() +
                        ' ' +
                        order?.data?.currency
                      : 'N/A',
                  total:
                    order?.data?.amountNetTotal !== undefined && multiplier
                      ? order?.data?.amountNetTotal
                          .div(multiplier)
                          .add(order.data.taxTotal.div(multiplier))
                          .toNumber() +
                        ' ' +
                        order?.data?.currency
                      : 'N/A'
                }),
                window.location.origin
              );
            }}
            src={`${process.env.PUBLIC_URL}/assets/html/OrderRecieptIframe.html`}
            title="test"
            width="100%"
            height="100%"
          />
        </Stack>
      )}
      <AcContentWrapper>
        {!order.isLoading && order.data ? (
          <Stack direction="column" gap={1}>
            <span className="header-title">
              {!isFree ? 'TOTAL PAYMENT' : 'PRICE'}
            </span>
            <Stack
              style={{ color: 'black' }}
              direction="row"
              alignItems="center"
              gap={1}
            >
              <span className="order-price">
                {isFree ? (
                  <b>Free</b>
                ) : order.data.amountInDollar ? (
                  <>
                    <b>
                      {order.data.amountNetTotal !== undefined && multiplier
                        ? `${order.data.amountNetTotal
                            .div(multiplier)
                            .add(order.data.taxTotal.div(multiplier))
                            .toNumber()} ${order.data.currency}`
                        : 'N/A'}
                    </b>
                  </>
                ) : (
                  'N/A'
                )}
              </span>
              <StatusLabel
                text={
                  isFree && orderStatus === EOrderStatus.charge_failed
                    ? chargeFailedFreeOrder
                    : statusDictionary[orderStatus as EOrderStatus]?.text
                }
                status={statusDictionary[orderStatus as EOrderStatus]?.status}
                prefixIcon={
                  statusDictionary[orderStatus as EOrderStatus]?.prefixIcon
                }
              />
            </Stack>
            <AcSeparator marginTop={'4px'} marginBottom={'16px'} />
            {isFree ? (
              <Stack direction="row" className="order-details">
                <Stack
                  direction="column"
                  style={{
                    wordWrap: 'break-word',
                    maxWidth: '200px'
                  }}
                >
                  <OrderDetailsCell
                    header="Date"
                    value={dayjs(order.data?.createdAt)
                      .utc()
                      .format(DATE_TIME_FORMAT)}
                  />
                  <OrderDetailsCell
                    header="Order ID"
                    value={orderId ? orderId.toString() : '-'}
                  />
                  <OrderDetailsCell
                    header="Player ID"
                    value={order.data.playerId ? order.data.playerId : '-'}
                  />
                  <OrderDetailsCell
                    header="Player Email"
                    value={
                      order.data.playerEmail ? order.data.playerEmail : '-'
                    }
                  />
                  <OrderDetailsCell
                    header="Purchase ID"
                    value={
                      order.data?.purchaseId ? order.data?.purchaseId : '-'
                    }
                  />
                </Stack>
                <Stack direction="column">
                  <OrderDetailsCell
                    header="Order external ID"
                    value={
                      order.data?.publisherPurchaseId
                        ? order.data?.publisherPurchaseId
                        : '-'
                    }
                  />
                  <OrderDetailsCell
                    header="Offer name"
                    value={order.data.bundleName ? order.data.bundleName : '-'}
                  />
                  <OrderDetailsCell
                    header="Offer external ID"
                    value={order.data.bundleSku ? order.data.bundleSku : '-'}
                  />
                </Stack>
                <Stack direction="column">
                  <OrderDetailsCell
                    header="Product sequence"
                    value={
                      order.data.offerSnapshot.sequenceIndex
                        ? order.data.offerSnapshot.sequenceIndex
                        : '-'
                    }
                  />
                  <OrderDetailsCell
                    header="Project"
                    value={
                      getCurrentProject(order?.data?.offerSnapshot?.publisherId)
                        ?.publisherName ?? '-'
                    }
                  />
                </Stack>
              </Stack>
            ) : (
              <Stack direction="row" className="order-details">
                <Stack
                  direction="column"
                  style={{
                    wordWrap: 'break-word',
                    maxWidth: '200px'
                  }}
                >
                  <OrderDetailsCell
                    header="Date"
                    value={dayjs(order.data?.createdAt)
                      .utc()
                      .format('DD/MM/YYYY HH:mm:ss')}
                  />
                  <OrderDetailsCell
                    header="Country"
                    value={
                      order.data?.playerGeo?.countryCode2
                        ? order.data?.playerGeo?.countryCode2
                        : '-'
                    }
                  />
                  <OrderDetailsCell
                    header="State"
                    value={
                      order.data?.state &&
                      order.data?.playerGeo?.countryCode2 === 'US'
                        ? order.data?.state
                        : '-'
                    }
                  />
                  {order.data?.zipCode && (
                    <OrderDetailsCell
                      header="Zip Code"
                      value={order.data?.zipCode}
                    />
                  )}
                </Stack>

                <Stack direction="column">
                  <OrderDetailsCell
                    header="Offer name"
                    value={order.data.bundleName ? order.data.bundleName : '-'}
                  />
                  <OrderDetailsCell
                    header="Offer External ID"
                    value={order.data.bundleSku ? order.data.bundleSku : '-'}
                  />
                  <OrderDetailsCell
                    header="Subtotal"
                    tooltip="Amount before tax and discounts"
                    value={
                      order.data.baseNetPrice
                        ? getFormattedNumberWithCurrency(
                            order.data.baseNetPrice,
                            currencyCodeToCurrencyMap.get(order.data.currency)
                          )
                        : '-'
                    }
                  />
                  {!!order.data.discountAmount && (
                    <OrderDetailsCell
                      header="Applied discount"
                      value={`-${getFormattedNumberWithCurrency(
                        order.data.discountAmount.abs(),
                        currencyCodeToCurrencyMap.get(
                          order.data.offerSnapshot.currencyCode
                        )
                      )}`}
                    />
                  )}
                </Stack>

                <Stack direction="column">
                  <OrderDetailsCell
                    header="Player Email"
                    value={
                      order.data.playerEmail ? order.data.playerEmail : '-'
                    }
                  />
                  <OrderDetailsCell
                    header="Payment method"
                    value={getPaymentMethod()}
                  />
                  <OrderDetailsCell
                    header="Tax rate"
                    value={
                      taxPercentage !== undefined ? `${taxPercentage}%` : '-'
                    }
                  />
                </Stack>

                <Stack direction="column">
                  <OrderDetailsCell
                    header="Player ID"
                    value={order.data.playerId ? order.data.playerId : '-'}
                  />
                  <OrderDetailsCell
                    header="Order ID"
                    value={orderId ? orderId.toString() : '-'}
                  />
                  <OrderDetailsCell
                    header="Publisher transaction ID"
                    value={
                      order.data?.publisherPurchaseId
                        ? order.data?.publisherPurchaseId
                        : '-'
                    }
                  />
                  <OrderDetailsCell
                    header="Project"
                    value={
                      getCurrentProject(order?.data?.offerSnapshot?.publisherId)
                        ?.publisherName ?? '-'
                    }
                  />
                  <OrderDetailsCell
                    header="Receipt number"
                    value={
                      fetchFeatureFlags.data?.featureFlags
                        ?.dashboard_generate_receipt ? (
                        <Tooltip title="Download Receipt as image" arrow>
                          {!isGeneratingReceipt ? (
                            <Button
                              variant="text"
                              disableRipple
                              onClick={() => {
                                setGenerateReceipt(true);
                                setIsGeneratingReceipt(true);
                              }}
                              sx={{
                                padding: 0,
                                minWidth: 0,
                                textTransform: 'none',
                                fontWeight: 400,
                                '&:hover': {
                                  backgroundColor: 'transparent'
                                }
                              }}
                            >
                              {order.data.receiptId
                                ? formatReceiptId(order.data.receiptId)
                                : '-'}
                            </Button>
                          ) : (
                            <Stack direction="row" alignItems="center" gap={1}>
                              <CircularProgress size={12} />
                              <Typography fontSize={12} color="primary">
                                Generating Receipt, please wait...
                              </Typography>
                            </Stack>
                          )}
                        </Tooltip>
                      ) : (
                        formatReceiptId(order?.data?.receiptId)
                      )
                    }
                  />
                  {isSubscriptionsAvailable && order.data.subscriptionId && (
                    <Stack direction="column">
                      <OrderDetailsCell
                        header="Subscription ID"
                        value={
                          order.data.subscriptionId
                            ? order.data.subscriptionId
                            : '-'
                        }
                      />
                    </Stack>
                  )}
                </Stack>
              </Stack>
            )}

            <AcSeparator marginTop={'4px'} marginBottom={'16px'} />
            {order.data.offerSnapshot && !isFree && (
              <>
                <h2 className="order__section-title">Payment Details</h2>
                <DataTable
                  headerHeight={0}
                  className="order__payment-details-table"
                  columns={[
                    {
                      field: 'paymentDetailName',
                      headerName: '',
                      width: 350
                    },
                    {
                      field: 'paymentDetailValue',
                      headerName: '',
                      flex: 1,
                      renderCell: (params) => {
                        const { value, valueInUSD } = params.value;
                        if (
                          (valueInUSD >= 0 && value === undefined) ||
                          value === valueInUSD
                        ) {
                          return getFormattedNumberWithCurrency(
                            valueInUSD,
                            currencyCodeToCurrencyMap.get('USD')
                          );
                        }
                        return (
                          <Stack direction="row" spacing={1}>
                            <Box>
                              {getFormattedNumberWithCurrency(
                                value,
                                currencyCodeToCurrencyMap.get(
                                  order.data.currency
                                )
                              )}
                            </Box>
                            <img
                              src={`./assets/images/arrow_right.svg`}
                              alt="dots"
                            />
                            <Box>
                              {getFormattedNumberWithCurrency(
                                valueInUSD,
                                currencyCodeToCurrencyMap.get('USD')
                              )}
                            </Box>
                          </Stack>
                        );
                      }
                    }
                  ]}
                  rows={[
                    {
                      paymentDetailName: 'Collected',
                      paymentDetailValue: {
                        value: order.data.amountNetTotal
                          .add(order.data.taxTotal)
                          .toNumber(),
                        valueInUSD: order.data.amountInDollar.toNumber()
                      }
                    },
                    {
                      paymentDetailName: 'Tax Amount',
                      paymentDetailValue: {
                        value: order.data.taxTotal.toNumber(),
                        valueInUSD: order.data.taxAmountInDollar.toNumber()
                      }
                    },
                    {
                      paymentDetailName: 'Exchange Cost',
                      paymentDetailValue: {
                        valueInUSD: order.data.currencyExchangeCost
                      }
                    },
                    {
                      paymentDetailName: 'Estimated Fee Amount',
                      paymentDetailValue: {
                        valueInUSD: order.data.estimatedAppchargeFee
                      }
                    },
                    {
                      paymentDetailName: 'Estimated Net Revenue',
                      paymentDetailValue: {
                        valueInUSD: order.data.estimatedPublisherNetAmount
                      }
                    }
                  ].map((item, index) => ({ ...item, _id: index }))}
                  loading={false}
                ></DataTable>
              </>
            )}

            {order.data.promoCodeName && !isFree && (
              <>
                <Box mt="1.5em" />
                <h2 className="order__section-title">Discounts</h2>
                <DataTable
                  className="order__products-table"
                  columns={[
                    {
                      field: 'name',
                      headerName: 'Promo code',
                      flex: 1
                    },
                    {
                      field: 'discount',
                      headerName: 'Discount',
                      flex: 1
                    }
                  ]}
                  rows={[
                    {
                      _id: 0,
                      name: order.data.promoCodeName,
                      discount: `${order.data.discountRatePoints}%`
                    }
                  ]}
                  loading={false}
                ></DataTable>
              </>
            )}

            {order.data.products.length !== 0 && (
              <>
                <Box mt="1.5em" />
                <h2 className="order__section-title">Products</h2>
                <DataTable
                  className="order__products-table"
                  columns={[
                    {
                      field: 'name',
                      headerName: 'Name',
                      flex: 1
                    },
                    {
                      field: 'sku',
                      headerName: 'External ID',
                      flex: 1
                    },
                    {
                      field: 'quantity',
                      headerName: isFree ? 'Amount' : 'Quantity',
                      flex: 1
                    }
                  ]}
                  rows={order.data.products.map((product, index) => ({
                    _id: product?.sku + index,
                    name: product.name,
                    sku: product?.sku || 'N/A',
                    quantity: product.quantity
                  }))}
                  loading={false}
                ></DataTable>
              </>
            )}
            {order.data.history && (
              <>
                <Box mt="1.5em" />
                <h2 className="order__section-title">History</h2>
                <DataTable
                  className="order__products-table"
                  columns={[
                    {
                      field: 'date',
                      headerName: 'Date',
                      flex: 1
                    },
                    {
                      field: 'status',
                      headerName: 'Status',
                      flex: 1,
                      renderCell: (params) => (
                        <span>
                          {isFree &&
                          params.value.toLowerCase() ===
                            EOrderStatus.charge_failed
                            ? chargeFailedFreeOrder
                            : statusDictionary[
                                params.value.toLowerCase() as EOrderStatus
                              ]?.text}
                        </span>
                      )
                    },
                    {
                      field: 'event',
                      headerName: 'Event',
                      flex: 1
                    }
                  ]}
                  rows={order.data.history.map((history, i) => ({
                    _id: i,
                    date: history.createdAt
                      ? dayjs(history.createdAt)
                          .utc()
                          .format('DD/MM/YYYY HH:mm:ss')
                      : '',
                    status: history.status
                      ? capitalizeFirstLetter(history.status)
                      : '',
                    event: history.event
                      ? capitalizeFirstLetter(history.event)
                      : ''
                  }))}
                  loading={false}
                ></DataTable>
              </>
            )}
            {Boolean(sessionMetadataEntries.length) && (
              <>
                <Box mt="1.5em" />
                <h2 className="order__section-title">Metadata</h2>
                <DataTable
                  getRowHeight={() => 'auto'}
                  className="order__products-table order__metadata-table"
                  columns={[
                    {
                      field: 'key',
                      headerName: 'Key',
                      flex: 1
                    },
                    {
                      field: 'value',
                      headerName: 'Value',
                      flex: 1
                    }
                  ]}
                  rows={sessionMetadataEntries.map(([key, value], i) => ({
                    _id: i,
                    key,
                    value
                  }))}
                  loading={false}
                ></DataTable>
              </>
            )}
            {showDisputeEvidences && (
              <DisputeEvidence orderParams={orderParams} />
            )}
          </Stack>
        ) : !order.isError ? (
          <Stack
            width="100%"
            height="calc(100vh - 150px)"
            justifyContent="center"
            alignItems="center"
          >
            <CircularProgress />
          </Stack>
        ) : (
          <Stack
            width="100%"
            height="calc(100vh - 150px)"
            justifyContent="center"
            alignItems="center"
          >
            <ErrorBox description="Something went wrong fetching the order" />
          </Stack>
        )}
        {order.data && (
          <DialogModal
            width="392px"
            isOpen={showRefundDialog}
            headline="Refund payment"
            text="Refunds take 5-10 days to appear on a customer's statement."
            buttons={[
              {
                text: 'Cancel',
                color: EButtonColor.SECONDARY,
                variant: 'outlined',
                func: () => {
                  setShowRefundDialog(false);
                }
              },
              {
                text: 'Refund',
                color: EButtonColor.PRIMARY,
                variant: 'contained',
                disabled: isDeleting,
                func: () => {
                  setIsDeleting(true);
                  makeRefund.mutate(
                    { orderId: orderId! },
                    {
                      onSuccess: () => {
                        showToast({
                          message: `Order ${orderId!} Refund attempt sent`,
                          status: EToasterStatus.SUCCESS
                        });
                        setOrderStatus(EOrderStatus.payment_refund_pending);
                      },
                      onError: (data: any) => {
                        showToast({
                          message: "Couldn't refund order",
                          status: EToasterStatus.ERROR
                        });
                      },
                      onSettled: () => {
                        setIsDeleting(false);
                        setShowRefundDialog(false);
                      }
                    }
                  );
                }
              }
            ]}
            closeDialog={() => setShowRefundDialog(false)}
            content={
              <Stack>
                <Box>
                  <Typography fontSize="12px" marginTop="16px">
                    Refund amount
                  </Typography>
                  <Typography fontSize="20px">
                    <b>
                      {order.data.amountNetTotal !== undefined && multiplier
                        ? `${order.data.amountNetTotal
                            .div(multiplier)
                            .add(order.data.taxTotal.div(multiplier))
                            .toNumber()} ${order.data.currency}`
                        : 'N/A'}
                    </b>
                  </Typography>
                </Box>
              </Stack>
            }
          />
        )}
      </AcContentWrapper>
    </AcViewWrapper>
  );
};

export default OrderDetails;
